// Modello puro per l'editor inline delle menzioni (`MentionTextarea`).
//
// Il contratto verso backend, validatori e assistente AI resta invariato:
// `@[Nome Cognome](uuid)` nel valore serializzato, mentre nell'editor l'utente
// vede e manipola direttamente il badge atomico `@Nome Cognome` senza mai
// esporre gli UUID identificativi.

const MENTION_TOKEN_RE = /@\[([^\]]{1,80})\]\(([0-9a-fA-F-]{36})\)/g;
const DISPLAY_MENTION_RE = /@\[([^\]]{1,80})\]\(([0-9a-fA-F-]{36})\)|@\[([^\]]{1,80})\]\(([0-9a-fA-F-]{0,35})\)?$/g;

/**
 * Costruisce il nome visualizzato e il token canonico `@[Nome](uuid)` per un utente.
 * @param {{ id?: string, nome?: string, cognome?: string, email?: string }} user
 * @returns {{ display: string, token: string }}
 */
export function formatMentionToken(user) {
  const fullName = `${user?.nome || ''} ${user?.cognome || ''}`
    .replace(/[\[\]\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const fallback = (user?.email || 'Utente').split('@')[0].replace(/[\[\]\r\n]+/g, '').trim() || 'Utente';
  const display = (fullName || fallback).slice(0, 80);
  const id = String(user?.id || '').trim();
  return {
    display,
    token: `@[${display}](${id})`,
  };
}

/**
 * Suddivide una stringa canonica in segmenti ordinati di testo e menzioni.
 * @param {string} value
 * @returns {Array<{ type: 'text', value: string } | { type: 'mention', name: string, userId: string, raw: string }>}
 */
export function parseMentionSegments(value) {
  if (!value || typeof value !== 'string') return [];
  const normalized = value.replace(/\r\n/g, '\n');
  const segments = [];
  const re = new RegExp(MENTION_TOKEN_RE.source, 'g');
  let lastIdx = 0;
  let match;

  while ((match = re.exec(normalized)) !== null) {
    if (match.index > lastIdx) {
      segments.push({ type: 'text', value: normalized.slice(lastIdx, match.index) });
    }
    segments.push({
      type: 'mention',
      name: match[1],
      userId: match[2],
      raw: match[0],
    });
    lastIdx = match.index + match[0].length;
  }

  if (lastIdx < normalized.length) {
    segments.push({ type: 'text', value: normalized.slice(lastIdx) });
  }

  return segments;
}

/**
 * Ricompone un elenco di segmenti nella stringa canonica `@[Nome](uuid)`.
 * @param {Array<{ type: string, value?: string, name?: string, userId?: string, raw?: string }>} segments
 * @returns {string}
 */
export function segmentsToCanonical(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return '';
  return segments
    .map((seg) => (seg.type === 'mention' ? `@[${seg.name}](${seg.userId})` : seg.value || ''))
    .join('');
}

/**
 * Converte una stringa con token `@[Nome](uuid)` in testo leggibile `@Nome`
 * (usato per la copia negli appunti `text/plain` verso applicazioni esterne).
 * @param {string} value
 * @returns {string}
 */
export function canonicalToPlainDisplay(value) {
  if (!value || typeof value !== 'string') return '';
  return parseMentionSegments(value)
    .map((seg) => (seg.type === 'mention' ? `@${seg.name}` : seg.value))
    .join('');
}

/**
 * Suddivide un testo per il rendering in `MentionRenderer`, convertendo sia i token
 * completi `@[Nome](uuid)` sia eventuali token finali troncati a metà UUID da notifiche storiche.
 * @param {string} body
 * @returns {string | Array<{ type: 'text', value: string } | { type: 'mention', name: string, userId: string | null }>}
 */
export function parseDisplayMentions(body) {
  if (!body || typeof body !== 'string') return '';
  const parts = [];
  const re = new RegExp(DISPLAY_MENTION_RE.source, 'g');
  let lastIdx = 0;
  let m;
  while ((m = re.exec(body)) !== null) {
    if (m.index > lastIdx) parts.push({ type: 'text', value: body.slice(lastIdx, m.index) });
    if (m[1] !== undefined) {
      parts.push({ type: 'mention', name: m[1], userId: m[2] });
    } else {
      parts.push({ type: 'mention', name: m[3], userId: null });
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) parts.push({ type: 'text', value: body.slice(lastIdx) });
  return parts;
}

/**
 * Tronca un elenco di segmenti entro `maxLen` caratteri canonici senza mai
 * spezzare a metà un token `@[Nome](uuid)` né una coppia surrogata UTF-16.
 * @param {Array<{ type: string, value?: string, name?: string, userId?: string, raw?: string }>} segments
 * @param {number} maxLen
 * @returns {Array<{ type: 'text', value: string } | { type: 'mention', name: string, userId: string, raw: string }>}
 */
export function truncateMentionSegments(segments, maxLen) {
  if (!Array.isArray(segments) || segments.length === 0) return [];
  if (typeof maxLen !== 'number' || !Number.isFinite(maxLen)) return segments;
  if (maxLen <= 0) return [];

  const result = [];
  let used = 0;

  for (const seg of segments) {
    if (used >= maxLen) break;
    if (seg.type === 'mention') {
      const raw = seg.raw || `@[${seg.name}](${seg.userId})`;
      if (used + raw.length > maxLen) break;
      result.push({ type: 'mention', name: seg.name, userId: seg.userId, raw });
      used += raw.length;
    } else if (seg.value) {
      const remaining = maxLen - used;
      if (seg.value.length <= remaining) {
        result.push({ type: 'text', value: seg.value });
        used += seg.value.length;
      } else {
        let sliced = seg.value.slice(0, remaining);
        const lastCode = sliced.charCodeAt(sliced.length - 1);
        if (lastCode >= 0xd800 && lastCode <= 0xdbff) {
          sliced = sliced.slice(0, -1);
        }
        if (sliced) {
          result.push({ type: 'text', value: sliced });
          used += sliced.length;
        }
        break;
      }
    }
  }

  return result;
}

/**
 * Tronca una stringa canonica entro `maxLen` senza spezzare token `@[Nome](uuid)`.
 * @param {string} value
 * @param {number} maxLen
 * @returns {string}
 */
export function truncateMentionValue(value, maxLen) {
  if (!value || typeof value !== 'string') return '';
  if (typeof maxLen !== 'number' || !Number.isFinite(maxLen) || value.length <= maxLen) {
    return value.replace(/\r\n/g, '\n');
  }
  return segmentsToCanonical(truncateMentionSegments(parseMentionSegments(value), maxLen));
}

/**
 * Rileva una query `@menzione` attiva immediatamente prima del cursore dentro un nodo di testo.
 * Non scatta sugli indirizzi email (`nome@dominio.it`) perché richiede inizio riga/spazio prima di `@`.
 * @param {string} textBeforeCaret - Sottostringa del nodo di testo dall'indice 0 fino al cursore
 * @param {string} [boundaryBeforeNode=' '] - Carattere logico che precede il nodo di testo
 * @returns {{ startOffset: number, endOffset: number, query: string } | null}
 */
export function detectMentionQuery(textBeforeCaret, boundaryBeforeNode = ' ') {
  if (typeof textBeforeCaret !== 'string' || textBeforeCaret.length === 0) return null;

  for (let i = textBeforeCaret.length - 1; i >= 0; i -= 1) {
    const ch = textBeforeCaret[i];
    if (ch === '@') {
      const prev = i === 0 ? boundaryBeforeNode : textBeforeCaret[i - 1];
      if (!prev || /\s/.test(prev)) {
        return {
          startOffset: i,
          endOffset: textBeforeCaret.length,
          query: textBeforeCaret.slice(i + 1),
        };
      }
      return null;
    }
    if (/\s/.test(ch) || ch === '[' || ch === ']' || ch === ')') {
      return null;
    }
  }

  return null;
}

function getAttr(node, camelKey, dataAttr) {
  if (node?.dataset && node.dataset[camelKey] !== undefined) {
    return node.dataset[camelKey];
  }
  if (typeof node?.getAttribute === 'function') {
    const val = node.getAttribute(dataAttr);
    if (val !== null && val !== undefined) return val;
  }
  return undefined;
}

function isSinglePlaceholderBrBlock(node) {
  const children = Array.from(node?.childNodes || []);
  if (children.length !== 1) return false;
  const only = children[0];
  return only?.nodeType === 1 && String(only.nodeName || '').toUpperCase() === 'BR';
}

/**
 * Serializza ricorsivamente il DOM (o un `DocumentFragment` selezionato) dell'editor
 * nella rappresentazione canonica con token `@[Nome](uuid)`.
 * @param {Node | null | undefined} rootNode
 * @returns {string}
 */
export function serializeEditorNode(rootNode) {
  if (!rootNode) return '';

  const walkChildren = (parent) => {
    let out = '';
    const children = Array.from(parent?.childNodes || []);

    for (let idx = 0; idx < children.length; idx += 1) {
      const child = children[idx];
      if (!child) continue;

      if (child.nodeType === 3) {
        out += String(child.nodeValue || '')
          .replace(/\u200B/g, '')
          .replace(/\r\n/g, '\n');
        continue;
      }

      if (child.nodeType === 11) {
        out += walkChildren(child);
        continue;
      }

      if (child.nodeType !== 1) continue;

      const mentionId = getAttr(child, 'mentionId', 'data-mention-id');
      const mentionName = getAttr(child, 'mentionName', 'data-mention-name');
      if (mentionId && mentionName) {
        out += `@[${mentionName}](${mentionId})`;
        continue;
      }

      const tag = String(child.nodeName || '').toUpperCase();
      if (tag === 'BR') {
        const isTrailing = getAttr(child, 'trailingBreak', 'data-trailing-break') === 'true';
        if (!isTrailing) out += '\n';
        continue;
      }

      if (tag === 'DIV' || tag === 'P') {
        if (idx > 0 && !out.endsWith('\n')) {
          out += '\n';
        }
        if (isSinglePlaceholderBrBlock(child)) {
          if (idx === 0) out += '\n';
          continue;
        }
        out += walkChildren(child);
        continue;
      }

      out += walkChildren(child);
    }

    return out;
  };

  if (rootNode.nodeType === 3) {
    return String(rootNode.nodeValue || '').replace(/\u200B/g, '').replace(/\r\n/g, '\n');
  }

  return walkChildren(rootNode);
}
